import moment from "moment";
import IOrder_Item_Option from "../models/IOrder_Item_Option";
import IOrder_Item_PickListDTO from "../models/DTO/IOrder_Item_PickListDTO";
import IOrdersDetailDTO from "../models/DTO/IOrdersDetailDTO";

/**
 * Mirrors the item option helpers in @orderwerks/documents (orderwerks-shared:
 * packages/documents/src/pick-list/groupItems.ts), which renders the emailed pick list PDF. Change both or the
 * screen and the PDF will differ.
 */

/** One assigned item option as printed: its name and the value chosen on the order line. */
export interface IPickItemOption {
    name: string;
    value: string;
}

/** An option value as text. Values are stored as JSON (strings in practice); a list prints comma separated. */
export const formatItemOptionValue = (value: unknown): string => {
    if (value === null || value === undefined) {
        return "";
    }

    if (Array.isArray(value)) {
        return value.map(formatItemOptionValue).join(", ");
    }

    if (typeof value === "object") {
        return JSON.stringify(value);
    }

    return String(value);
};

/** The line's assigned item options in the order the API sent them, for the Item Options column and the CSV. */
export const getPickItemOptions = (item: Pick<IOrder_Item_PickListDTO, "Order_Item_Options">): IPickItemOption[] =>
    (item.Order_Item_Options || []).map((option: IOrder_Item_Option) => ({
        name: option.Item_Option?.name || "",
        value: formatItemOptionValue(option.value),
    }));

/** The options as one line of text ("Finish: Black, Shelves: 4"), for the CSV export. */
export const formatPickItemOptions = (item: Pick<IOrder_Item_PickListDTO, "Order_Item_Options">): string =>
    getPickItemOptions(item)
        .map((option) => (option.name ? `${option.name}: ${option.value}` : option.value))
        .join(", ");

/**
 * A key that tells pick list lines apart: catalog item, UoM set unit, delivery date and item options. Aggregated
 * lines carry no guid, and with those groupings one catalog item can have several lines.
 */
export const getPickItemKey = (item: IOrder_Item_PickListDTO): string => {
    const deliveryDate = item.delivery_date instanceof Date ? item.delivery_date.toISOString() : item.delivery_date || "";
    const options = (item.Order_Item_Options || []).map(
        (option) => `${option.id ?? option.item_option_id ?? option.Item_Option?.name ?? ""}=${formatItemOptionValue(option.value)}`
    );

    // Structured, not delimiter-joined: an option value may itself contain "|" or "::".
    return JSON.stringify([item.catalog_item_id ?? "", item.uom_set_id ?? "base", deliveryDate, options]);
};

/**
 * The one delivery date every order on the pick list shares (compared as calendar days, the way delivery_date
 * is stored: midnight UTC), or null when any order has none or they differ. Shown in the repeated order
 * header, so a picker sees the day without reading the column.
 */
export const getSharedDeliveryDate = (orders: Pick<IOrdersDetailDTO, "delivery_date">[]): string | Date | null => {
    if (orders.length === 0) {
        return null;
    }

    const days = orders.map((order) => (order.delivery_date ? moment.utc(order.delivery_date).format("YYYY-MM-DD") : ""));

    return days[0] && days.every((day) => day === days[0]) ? (orders[0].delivery_date ?? null) : null;
};
